import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { API_BASE } from '../utils/config';
import { apiFetch } from '../utils/apiFetch';

type PaymentMethod = 'card' | 'cod';

const CheckoutPage: React.FC = () => {
  const { cartItems, cartTotal, refreshCart } = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();

  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod>('card');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const tax = cartTotal * 0.18;
  const grandTotal = cartTotal + tax;

  const placeCardOrder = async () => {
    const res = await apiFetch(`${API_BASE}/checkoutsession`, {
      method: 'POST',
      body: JSON.stringify({
        items: cartItems.map((item) => ({
          name: item.product_name,
          product_id: item.product_id,
          price: item.selling_price,
          quantity: item.quantity,
        })),
        location: window.location.origin,
      }),
    });

    if (res.ok) {
      const data = await res.json();
      if (data.url) {
        window.location.href = data.url;
      } else {
        await refreshCart();
        navigate('/orders');
      }
    } else {
      throw new Error('Payment session failed');
    }
  };

  const placeCODOrder = async () => {
    const today = new Date().toISOString().split('T')[0];
    const deliveryDate = new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];

    const res = await apiFetch(`${API_BASE}/order`, {
      method: 'POST',
      body: JSON.stringify({
        customer_id: user!.userId,
        billing_date: today,
        delivery_date: deliveryDate,
        billed_by: user!.userId,
        bill_to_name: 'Customer',
        is_gst_enabled: true,
        payment_method: 'Cash',
        amount_paid: 0,
        cartItems: cartItems.map((item) => ({
          product_name: item.product_name,
          product_id: item.product_id,
          selling_price: item.selling_price,
          quantity: item.quantity,
          category_id: item.category_id || 1,
          gst_value: 18,
          fitting: false,
          lens_attributes: null,
        })),
      }),
    });

    if (res.ok) {
      await refreshCart();
      navigate('/orders');
    } else {
      const data = await res.json().catch(() => ({}));
      throw new Error(data.detail || 'Order placement failed');
    }
  };

  const handlePlaceOrder = async () => {
    setLoading(true);
    setError('');
    try {
      if (paymentMethod === 'card') {
        await placeCardOrder();
      } else {
        await placeCODOrder();
      }
    } catch (err: any) {
      setError(err.message || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (!user) {
    return (
      <div className="checkout-page">
        <div className="empty-state">
          <p>Please sign in to checkout.</p>
          <Link to="/cart" className="btn btn--primary">Back to Cart</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="checkout-page">
      <div className="breadcrumb">
        <Link to="/">Home</Link>
        <span className="breadcrumb__sep">›</span>
        <Link to="/cart">Cart</Link>
        <span className="breadcrumb__sep">›</span>
        <Link to="/lens">Lens Selection</Link>
        <span className="breadcrumb__sep">›</span>
        <Link to="/address">Delivery</Link>
        <span className="breadcrumb__sep">›</span>
        <span>Payment</span>
      </div>

      <h1 className="checkout-page__title">Checkout</h1>

      <div className="checkout-layout">
        {/* Left: payment */}
        <div className="checkout-forms">
          {/* Payment method */}
          <div className="checkout-section">
            <h3>Payment Method</h3>
            <div className="payment-methods">
              <label className={`payment-option ${paymentMethod === 'card' ? 'payment-option--selected' : ''}`}>
                <input type="radio" name="payment" value="card" checked={paymentMethod === 'card'} onChange={() => setPaymentMethod('card')} />
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ flexShrink: 0 }}>
                  <rect x="1" y="4" width="22" height="16" rx="2"/><line x1="1" y1="10" x2="23" y2="10"/>
                </svg>
                <span>Credit / Debit Card</span>
              </label>
              <label className={`payment-option ${paymentMethod === 'cod' ? 'payment-option--selected' : ''}`}>
                <input type="radio" name="payment" value="cod" checked={paymentMethod === 'cod'} onChange={() => setPaymentMethod('cod')} />
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ flexShrink: 0 }}>
                  <rect x="1" y="3" width="15" height="13" rx="1"/><path d="M16 8h4l3 3v5h-7V8z"/><circle cx="5.5" cy="18.5" r="2.5"/><circle cx="18.5" cy="18.5" r="2.5"/>
                </svg>
                <span>Cash on Delivery</span>
              </label>
            </div>
            {paymentMethod === 'cod' && (
              <p style={{ marginTop: 10, fontSize: 13, color: '#6b7280', padding: '10px 12px', background: '#f9fafb', borderRadius: 8, border: '1px solid #e5e7eb' }}>
                Pay with cash when your order is delivered. Our delivery partner will collect the amount.
              </p>
            )}
          </div>

          {error && <div className="modal-error">{error}</div>}
        </div>

        {/* Right: order summary */}
        <div className="order-summary checkout-summary">
          <h3 className="order-summary__title">Order Summary</h3>

          <div className="checkout-items">
            {cartItems.map((item) => (
              <div key={item.id} className="checkout-item">
                <img src={item.image_uri?.[0] || ''} alt={item.product_name} className="checkout-item__img" />
                <div className="checkout-item__info">
                  <p className="checkout-item__name">{item.product_name}</p>
                  <p className="checkout-item__qty">x{item.quantity}</p>
                </div>
                <span className="checkout-item__price">₹{Math.round(item.selling_price * item.quantity).toLocaleString()}</span>
              </div>
            ))}
          </div>

          <div className="order-summary__rows">
            <div className="order-summary__row"><span>Subtotal</span><span>₹{Math.round(cartTotal).toLocaleString()}</span></div>
            <div className="order-summary__row"><span>Tax (18% GST)</span><span>₹{Math.round(tax).toLocaleString()}</span></div>
          </div>

          <div className="order-summary__total">
            <span>Total</span>
            <span>₹{Math.round(grandTotal).toLocaleString()}</span>
          </div>

          <button
            className="btn btn--primary order-summary__checkout-btn"
            onClick={handlePlaceOrder}
            disabled={loading || cartItems.length === 0}
          >
            {loading ? 'Processing...' : paymentMethod === 'cod' ? 'Place Order' : 'Pay with Card'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CheckoutPage;
